/**
 * Sistema de Features
 * Gerencia detecção e suporte de features web específicas
 */

import { 
  IFeatureService, 
  WebFeature, 
  FeatureReport, 
  FeaturePermissionCheck 
} from '../interfaces/feature_service';
import { PermissionType } from '../interfaces/permission_service';

export class FeatureSystem implements IFeatureService {
  readonly serviceName = 'Feature' as const;
  isInitialized = false;
  
  private features = new Map<string, WebFeature>();
  private platformInfo: any = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🔧 Inicializando Sistema de Features...');

    try {
      // Detectar informações da plataforma
      this.detectPlatformInfo();
      
      // Registrar features conhecidas
      this.registerWebFeatures();

      this.isInitialized = true;
      console.log('✅ Sistema de Features inicializado');
    } catch (error) {
      console.error('❌ Erro ao inicializar Sistema de Features:', error);
      throw error;
    }
  }

  private detectPlatformInfo(): void {
    const ua = navigator.userAgent;
    
    this.platformInfo = {
      userAgent: ua,
      vendor: navigator.vendor,
      language: navigator.language,
      languages: navigator.languages || [],
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      maxTouchPoints: navigator.maxTouchPoints || 0,
      hardwareConcurrency: navigator.hardwareConcurrency || 1,
      deviceMemory: (navigator as any).deviceMemory || 0,
      
      // Browser detection
      isChrome: /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor),
      isFirefox: /Firefox/.test(ua),
      isSafari: /Safari/.test(ua) && /Apple Computer/.test(navigator.vendor),
      isEdge: /Edg/.test(ua),
      isOpera: /OPR/.test(ua) || /Opera/.test(ua),
      
      // Device detection
      isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua),
      isTablet: /iPad|Android(?!.*Mobile)/i.test(ua),
      isDesktop: !/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
    };

    console.log('📱 Informações da plataforma detectadas:', this.platformInfo);
  }

  private registerWebFeatures(): void {
    const featuresToCheck = [
      // Location APIs
      {
        name: 'geolocation',
        check: () => 'geolocation' in navigator,
        requiresPermission: true,
        permissionType: PermissionType.GEOLOCATION
      },
      
      // Notification APIs
      {
        name: 'notifications',
        check: () => 'Notification' in window,
        requiresPermission: true,
        permissionType: PermissionType.NOTIFICATIONS
      },
      
      // Media APIs
      {
        name: 'camera',
        check: () => 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
        requiresPermission: true,
        permissionType: PermissionType.CAMERA
      },
      
      {
        name: 'microphone',
        check: () => 'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices,
        requiresPermission: true,
        permissionType: PermissionType.MICROPHONE
      },
      
      // Storage APIs
      {
        name: 'localStorage',
        check: () => this.checkLocalStorage(),
        requiresPermission: false
      },
      
      {
        name: 'sessionStorage',
        check: () => this.checkSessionStorage(),
        requiresPermission: false
      },
      
      {
        name: 'indexedDB',
        check: () => 'indexedDB' in window,
        requiresPermission: false
      },
      
      {
        name: 'persistentStorage',
        check: () => 'storage' in navigator && 'persist' in navigator.storage,
        requiresPermission: true,
        permissionType: PermissionType.PERSISTENT_STORAGE
      },
      
      // Clipboard API
      {
        name: 'clipboard',
        check: () => 'clipboard' in navigator,
        requiresPermission: true,
        permissionType: PermissionType.CLIPBOARD_READ
      },
      
      // Service Workers
      {
        name: 'serviceWorkers',
        check: () => 'serviceWorker' in navigator,
        requiresPermission: false
      },
      
      // Web Workers
      {
        name: 'webWorkers',
        check: () => 'Worker' in window,
        requiresPermission: false
      },
      
      // Device APIs
      {
        name: 'vibration',
        check: () => 'vibrate' in navigator,
        requiresPermission: false
      },
      
      {
        name: 'deviceOrientation',
        check: () => 'DeviceOrientationEvent' in window,
        requiresPermission: false
      },
      
      {
        name: 'deviceMotion',
        check: () => 'DeviceMotionEvent' in window,
        requiresPermission: false
      },
      
      // Fullscreen API
      {
        name: 'fullscreen',
        check: () => this.checkFullscreen(),
        requiresPermission: false
      },
      
      // WebRTC
      {
        name: 'webRTC',
        check: () => 'RTCPeerConnection' in window,
        requiresPermission: false
      },
      
      // WebGL
      {
        name: 'webGL',
        check: () => this.checkWebGL(),
        requiresPermission: false
      },
      
      {
        name: 'webGL2',
        check: () => this.checkWebGL2(),
        requiresPermission: false
      },
      
      // Payment API
      {
        name: 'paymentRequest',
        check: () => 'PaymentRequest' in window,
        requiresPermission: false
      },
      
      // Web Share API
      {
        name: 'webShare',
        check: () => 'share' in navigator,
        requiresPermission: false
      },
      
      // Bluetooth
      {
        name: 'bluetooth',
        check: () => 'bluetooth' in navigator,
        requiresPermission: false
      },
      
      // USB
      {
        name: 'usb',
        check: () => 'usb' in navigator,
        requiresPermission: false
      },
      
      // Gamepad
      {
        name: 'gamepad',
        check: () => 'getGamepads' in navigator,
        requiresPermission: false
      }
    ];

    for (const feature of featuresToCheck) {
      const webFeature: WebFeature = {
        name: feature.name,
        isSupported: feature.check(),
        requiresPermission: feature.requiresPermission,
        permissionType: feature.permissionType,
        notes: `Detectado automaticamente em ${new Date().toISOString()}`
      };

      this.features.set(feature.name, webFeature);
    }

    console.log(`🔍 ${this.features.size} features registradas`);
  }

  isSupported(featureName: string): boolean {
    const feature = this.features.get(featureName);
    return feature?.isSupported || false;
  }

  getFeature(featureName: string): WebFeature | null {
    return this.features.get(featureName) || null;
  }

  getSupportedFeatures(): Map<string, WebFeature> {
    const supported = new Map<string, WebFeature>();
    
    for (const [name, feature] of this.features) {
      if (feature.isSupported) {
        supported.set(name, feature);
      }
    }
    
    return supported;
  }

  getUnsupportedFeatures(): Map<string, WebFeature> {
    const unsupported = new Map<string, WebFeature>();
    
    for (const [name, feature] of this.features) {
      if (!feature.isSupported) {
        unsupported.set(name, feature);
      }
    }
    
    return unsupported;
  }

  async checkFeaturePermissions(featureName: string): Promise<FeaturePermissionCheck | null> {
    const feature = this.features.get(featureName);
    
    if (!feature) {
      return null;
    }

    if (!feature.requiresPermission || !feature.permissionType) {
      return {
        feature,
        hasPermission: true,
        canRequest: false
      };
    }

    try {
      // Verificar permissão atual
      const permission = await navigator.permissions.query({ 
        name: feature.permissionType as PermissionName 
      });

      return {
        feature,
        hasPermission: permission.state === 'granted',
        canRequest: permission.state === 'prompt'
      };
    } catch (error) {
      console.warn(`Não foi possível verificar permissão para ${featureName}:`, error);
      
      return {
        feature,
        hasPermission: false,
        canRequest: true
      };
    }
  }

  async requestFeaturePermission(featureName: string): Promise<boolean> {
    const feature = this.features.get(featureName);
    
    if (!feature || !feature.requiresPermission || !feature.permissionType) {
      return true; // Feature não requer permissão
    }

    try {
      switch (feature.permissionType) {
        case PermissionType.GEOLOCATION:
          return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              () => resolve(true),
              () => resolve(false),
              { timeout: 5000 }
            );
          });

        case PermissionType.NOTIFICATIONS:
          const permission = await Notification.requestPermission();
          return permission === 'granted';

        case PermissionType.CAMERA:
        case PermissionType.MICROPHONE:
          try {
            const constraints: MediaStreamConstraints = {};
            if (feature.permissionType === PermissionType.CAMERA) {
              constraints.video = true;
            }
            if (feature.permissionType === PermissionType.MICROPHONE) {
              constraints.audio = true;
            }
            
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            stream.getTracks().forEach(track => track.stop()); // Limpar stream
            return true;
          } catch {
            return false;
          }

        case PermissionType.PERSISTENT_STORAGE:
          if ('storage' in navigator && 'persist' in navigator.storage) {
            return await navigator.storage.persist();
          }
          return false;

        default:
          return false;
      }
    } catch (error) {
      console.error(`Erro ao solicitar permissão para ${featureName}:`, error);
      return false;
    }
  }

  generateFeatureReport(): FeatureReport {
    const supported: string[] = [];
    const unsupported: string[] = [];
    const needsPermission: string[] = [];

    for (const [name, feature] of this.features) {
      if (feature.isSupported) {
        supported.push(name);
        
        if (feature.requiresPermission) {
          needsPermission.push(name);
        }
      } else {
        unsupported.push(name);
      }
    }

    return {
      platform: this.platformInfo,
      supported,
      unsupported,
      needsPermission,
      total: this.features.size
    };
  }

  refreshFeatures(): void {
    console.log('🔄 Atualizando detecção de features...');
    this.features.clear();
    this.registerWebFeatures();
  }

  // Métodos auxiliares para verificação de features
  private checkLocalStorage(): boolean {
    try {
      return 'localStorage' in window && window.localStorage !== null;
    } catch {
      return false;
    }
  }

  private checkSessionStorage(): boolean {
    try {
      return 'sessionStorage' in window && window.sessionStorage !== null;
    } catch {
      return false;
    }
  }

  private checkFullscreen(): boolean {
    return 'requestFullscreen' in document.documentElement ||
           'webkitRequestFullscreen' in document.documentElement ||
           'mozRequestFullScreen' in document.documentElement ||
           'msRequestFullscreen' in document.documentElement;
  }

  private checkWebGL(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch {
      return false;
    }
  }

  private checkWebGL2(): boolean {
    try {
      const canvas = document.createElement('canvas');
      return !!canvas.getContext('webgl2');
    } catch {
      return false;
    }
  }

  getStatus(): any {
    const report = this.generateFeatureReport();
    
    return {
      serviceName: this.serviceName,
      isInitialized: this.isInitialized,
      platformInfo: this.platformInfo,
      totalFeatures: this.features.size,
      supportedCount: report.supported.length,
      unsupportedCount: report.unsupported.length,
      permissionRequiredCount: report.needsPermission.length,
      supportPercentage: Math.round((report.supported.length / report.total) * 100)
    };
  }

  cleanup(): void {
    this.features.clear();
    this.platformInfo = null;
    this.isInitialized = false;
    console.log('🧹 FeatureSystem limpo');
  }
}
